import { Injectable, LOCALE_ID, NgZone, inject } from '@angular/core';
import {
  Auth,
  ApplicationVerifier,
  ConfirmationResult,
  CustomParameters,
  User as FirebaseUser,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithEmailAndPassword,
  signInWithPhoneNumber,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  UserCredential,
  getRedirectResult,
  AuthErrorCodes,
} from '@angular/fire/auth';
import { setPersistence, browserLocalPersistence } from 'firebase/auth';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { FirebaseError } from '@angular/fire/app';
import { PersistenceService } from './persistence.service';
import { GetAdditionalUserInfoRequest } from '../models/requests/get-additional-user-info-request';
import { GetAdditionalUserInfoResponse } from '../models/responses/get-additional-user-info-response';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private locale: string = inject(LOCALE_ID);
  private auth: Auth = inject(Auth);
  private zone: NgZone = inject(NgZone);
  private router: Router = inject(Router);
  private currentUser$$: BehaviorSubject<AuthenticatedUser | null | undefined> = new BehaviorSubject<AuthenticatedUser | null | undefined>(undefined);
  private persistenceService: PersistenceService = inject(PersistenceService);
  constructor() {
    // Persistence setup (non-blocking)
    void setPersistence(this.auth, browserLocalPersistence).catch((err) => console.warn('Auth persistence fallback:', err));

    this.auth.languageCode = this.locale;

    // Use onIdTokenChanged so role/claims refresh without requiring sign-in/out
    let isFirstTokenChange = true;
    this.auth.onIdTokenChanged(async (firebaseUser: FirebaseUser | null) => {
      if (!firebaseUser) {
        this.zone.run(() => this.currentUser$$.next(null));
        isFirstTokenChange = true; // Reset flag on sign-out
        return;
      }

      try {
        // Force refresh on first token change (after sign-in) to ensure we get custom claims
        // set by before_user_signed_in blocking function
        const forceRefresh = isFirstTokenChange;
        isFirstTokenChange = false;
        const idTokenResult = await firebaseUser.getIdTokenResult(forceRefresh);
        const claims = (idTokenResult?.claims ?? {}) as Record<string, any>;
        const role = claims['role'] || 'user';

        const response: GetAdditionalUserInfoResponse = await this.fetchAdditionalUserInfo(firebaseUser);
        const additionalUserInfo: Record<string, any> = response.additionalUserInfo;
        const authenticationUser: AuthenticatedUser = new AuthenticatedUser(firebaseUser.uid, firebaseUser, role, additionalUserInfo);

        this.zone.run(() => {
          this.currentUser$$.next(authenticationUser);
        });
      } catch (error) {
        console.error('Error fetching ID token claims or extra info:', error);
        this.zone.run(() => {
          this.currentUser$$.next(null);
        });
      }
    });
  }

  public async continueWithGooglePopup(): Promise<UserCredential> {
    const provider = this.buildGoogleProvider();
    try {
      return await signInWithPopup(this.auth, provider);
    } catch (e: any) {
      if (this.isUserDisabledError(e)) {
        await this.redirectToAccountDisabled();
        return Promise.reject('USER_DISABLED');
      }
      if (this.isPermissionDeniedInternalError(e)) {
        await this.router.navigateByUrl('/unauthorised');
        return Promise.reject('UNAUTHORISED');
      }
      throw e;
    }
  }

  public continueWithGoogleRedirect(): Promise<void> {
    const provider: GoogleAuthProvider = this.buildGoogleProvider();
    return signInWithRedirect(this.auth, provider) as unknown as Promise<void>;
  }

  public async continueWithMicrosoftPopup(): Promise<UserCredential> {
    const provider: OAuthProvider = this.buildMicrosoftProvider();
    try {
      return await signInWithPopup(this.auth, provider);
    } catch (e: any) {
      if (this.isUserDisabledError(e)) {
        await this.redirectToAccountDisabled();
        return Promise.reject('USER_DISABLED');
      }
      throw e;
    }
  }

  public continueWithMicrosoftRedirect(): Promise<void> {
    const provider: OAuthProvider = this.buildMicrosoftProvider();
    return signInWithRedirect(this.auth, provider) as unknown as Promise<void>;
  }

  /**
   * Call this once on your login/splash page after load to complete redirect flows.
   * Returns a credential if this page load is the result of a redirect sign-in; otherwise null.
   */
  public async handleRedirectResult(): Promise<UserCredential | null> {
    try {
      const cred = await getRedirectResult(this.auth);
      return cred ?? null;
    } catch (e: any) {
      if (this.isUserDisabledError(e)) {
        await this.redirectToAccountDisabled();
        return Promise.reject('USER_DISABLED');
      }
      if (this.isPermissionDeniedInternalError(e)) {
        await this.router.navigateByUrl('/unauthorised');
        return Promise.reject('UNAUTHORISED');
      }
      throw e;
    }
  }

  public signInWithEmailAndPassword(email: string, password: string): Promise<UserCredential> {
    return signInWithEmailAndPassword(this.auth, email, password);
  }

  public signInWithPhoneNumber(phoneNumber: string, appVerifier: ApplicationVerifier): Promise<ConfirmationResult> {
    return signInWithPhoneNumber(this.auth, phoneNumber, appVerifier);
  }

  public signOut(): Promise<void> {
    return signOut(this.auth);
  }

  public get currentUser$(): Observable<AuthenticatedUser | null | undefined> {
    return this.currentUser$$.asObservable();
  }

  public get currentUser(): AuthenticatedUser | null | undefined {
    return this.currentUser$$.value;
  }

  /**
   * Force-refresh ID token & claims and update the subject.
   * Useful immediately after your backend sets new custom claims.
   */
  public async refreshClaims(): Promise<void> {
    const authUser = this.currentUser$$.value;
    const firebaseUser = authUser?.firebaseUser ?? this.auth.currentUser;
    if (!firebaseUser) return;

    const idTokenResult = await firebaseUser.getIdTokenResult(true);
    const claims = (idTokenResult?.claims ?? {}) as Record<string, any>;
    const role = claims['role'] || 'user';

    // Also refresh additional info if desired (optional):
    const extraInfo = await this.fetchAdditionalUserInfo(firebaseUser);

    const updated = new AuthenticatedUser(firebaseUser.uid, firebaseUser, role, extraInfo);
    this.zone.run(() => {
      this.currentUser$$.next(updated);
    });
  }

  public async fetchAdditionalUserInfo(_firebaseUser: FirebaseUser): Promise<GetAdditionalUserInfoResponse> {
    console.log('fetching');
    const request: GetAdditionalUserInfoRequest = {
      userId: _firebaseUser.uid,
    };
    return this.persistenceService.getAdditionalUserInfo(request);
  }

  private isPermissionDeniedInternalError(e: unknown): boolean {
    if (!(e instanceof FirebaseError)) return false;
    if (e.code !== 'auth/internal-error') return false;

    try {
      // Look for embedded JSON payload: ...{"error":{"status":"PERMISSION_DENIED",...}}...
      const match = e.message.match(/\{[\s\S]*\}\}/);
      if (match) {
        const parsed = JSON.parse(match[0]);
        if (parsed?.error?.status === 'PERMISSION_DENIED') return true;
      }
    } catch {
      // ignore parse errors
    }

    const status = (e as any)?.customData?.status || (e as any)?.customData?.error?.status || (e as any)?.serverResponse?.error?.status;
    return status === 'PERMISSION_DENIED';
  }

  public isUserDisabledError(error: unknown): error is FirebaseError {
    return error instanceof FirebaseError && error.code === AuthErrorCodes.USER_DISABLED;
  }

  public async redirectToAccountDisabled(): Promise<void> {
    await this.zone.run(() => this.router.navigateByUrl('/account-disabled'));
  }

  // --- Provider builders (DRY and consistent scopes/params) ---

  private buildGoogleProvider(): GoogleAuthProvider {
    const provider = new GoogleAuthProvider();
    provider.addScope('profile');
    provider.addScope('email');
    provider.setCustomParameters({ prompt: 'select_account' });
    return provider;
  }

  private buildMicrosoftProvider(): OAuthProvider {
    const provider: OAuthProvider = new OAuthProvider('microsoft.com');
    provider.addScope('profile');
    provider.addScope('email');
    provider.setCustomParameters({
      prompt: 'select_account',
    } as CustomParameters);
    return provider;
  }
}

export class AuthenticatedUser {
  constructor(
    private readonly _userId: string,
    private readonly _firebaseUser: FirebaseUser,
    private readonly _role: string,
    private readonly _additionalUserInfo?: Record<string, any>, // UserInformation bag
  ) {}

  get userId(): string {
    return this._userId;
  }

  get firebaseUser(): FirebaseUser {
    return this._firebaseUser;
  }

  get role(): string {
    return this._role ?? 'user';
  }

  get isAdmin(): boolean {
    return this.role === 'admin' || this.role === 'system-admin';
  }

  get isSystemAdmin(): boolean {
    return this.role === 'system-admin';
  }

  get isSuperAdmin(): boolean {
    // Deprecated: use isSystemAdmin instead
    return this.isSystemAdmin;
  }

  get additionalUserInfo(): Record<string, any> | undefined {
    return this._additionalUserInfo;
  }
}
